import { AccountType, TransactionTypeChar } from '@tassat/common-definitions';
import { UserAccountRecord } from '@tassat/fred-definitions';
import {
    AccountRecordExtended,
    AccountRecordMapped,
    WalletRecordExtended,
    WalletRecordMapped
} from '@tassat/fred-extended-definitions';
import { createSelector } from 'reselect';

import { AdminState } from 'Reducers/store/adminStore';
import { ClientState } from 'Reducers/store/clientStore';

import {
    getAccountClientGroups, getAccountPositions, getAccountRelationships, getAccounts
} from './common';

const getFirmId = (_: AdminState | ClientState, firmId: string) => firmId || '';
const getAccountId = (_: AdminState | ClientState, accountId: string) => accountId;
const getUserAccount = (_: AdminState | ClientState, userAccount: UserAccountRecord) => userAccount;
const getTransaction =
(state: AdminState | ClientState, selectedTransactionId: string) => state.transactions.transactions[selectedTransactionId];
const getPledge =
(state: AdminState | ClientState, selectedPledgeId: string) => state.pledges.pledges[selectedPledgeId];
const getPledgeDetail = (_: AdminState | ClientState, _selectedPledgeId: string, pledgeDetail: object) => pledgeDetail || {};
const getAccountOptionsWithFirmId =
(_: AdminState | ClientState, _firmId: string, accountOptions: { includeLocked?: boolean }) => accountOptions;

export const getAllAccountsArray = createSelector(getAccounts, (accounts) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => !account.IsDeleted && account.AccountTypeId === AccountType.CLIENT_BDA);
});

export const getOpenAccountsArray = createSelector(getAccounts, (accounts) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => !account.IsDeleted && !account.IsClosed && account.AccountTypeId === AccountType.CLIENT_BDA);
});

export const getAllWalletsArray = createSelector(getAccounts, (accounts) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => !account.IsDeleted && account.AccountTypeId === AccountType.CLIENT_EOA);
});

export const getOpenWalletsArray = createSelector(getAccounts, (accounts) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => !account.IsDeleted && !account.IsClosed && account.AccountTypeId === AccountType.CLIENT_EOA);
});

export const getAccountsForFirm = createSelector(
    [ getOpenAccountsArray, getFirmId, getAccountOptionsWithFirmId ],
    (walletList, firmId, accountOptions) => {
        const firmWallets = walletList.filter(acc => acc.FirmId === firmId);

        if (accountOptions.includeLocked === false) {
            return firmWallets.filter((wallet) => wallet.IsLocked === accountOptions.includeLocked);
        }

        return firmWallets;
    }
);

export const getWalletsForFirm = createSelector([ getOpenWalletsArray, getFirmId ], (walletList, firmId) => {
    return walletList.filter(acc => (acc.FirmId === firmId && acc.AccountTypeId === AccountType.CLIENT_EOA));
});

export const getAllBDAccountsByFirmId = createSelector([ getAccounts, getFirmId ], (accounts, firmId) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => account.FirmId === firmId && account.AccountTypeId === AccountType.CLIENT_BDA);
});

export const getOpenWalletssByFirmId = createSelector([ getAccounts, getFirmId ], (accounts, firmId) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => !account.IsDeleted &&
            account.FirmId === firmId &&
            !account.IsClosed &&
            account.AccountTypeId === AccountType.CLIENT_EOA);
});

export const getAllAccountsByFirmId = createSelector([ getAccounts, getFirmId ], (accounts, firmId) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => !account.IsDeleted && account.FirmId === firmId && account.AccountTypeId === AccountType.CLIENT_BDA);
});

export const getAllWalletsByFirmId = createSelector([ getAccounts, getFirmId ], (accounts, firmId) => {
    return Object.keys(accounts)
        .map(id => accounts[id])
        .filter(account => !account.IsDeleted && account.FirmId === firmId && account.AccountTypeId === AccountType.CLIENT_EOA);
});

export const getAllMappedAccountByAccountId = createSelector(
    [ getAccounts, getAccountId, getAccountPositions, getAccountRelationships, getAccountClientGroups ],
    (accounts, accountId, accountPositions, accountRelationshipsList, accountClientGroups) => {
        const accountRecord = Object.keys(accounts)
            .map(id => accounts[id])
            .find(account => !account.IsDeleted &&
                account.AccountId === accountId &&
                account.AccountTypeId === AccountType.CLIENT_BDA);

        const relatedWalllets = Object.keys(accountRelationshipsList)
            .map(id => accountRelationshipsList[id])
            .filter(relationship => relationship.AccountId2 === accountRecord?.AccountId ||
                relationship.AccountId1 === accountRecord?.AccountId);

        const linkedWallets = relatedWalllets.map(relation => {
            return Object.keys(accounts)
                .map(id => accounts[id])
                .find(account => (account.AccountId === relation.AccountId2 || account.AccountId === relation.AccountId1) &&
                    account.AccountTypeId === AccountType.CLIENT_EOA);
        });

        if (accountRecord) {
            const linkedAccountPositions = linkedWallets.reduce((acc, linkedWallet) => {
                acc[linkedWallet.AccountId] = accountPositions[linkedWallet.AccountId];

                return acc;
            }, {});
            const accountWithLinkedWallets: AccountRecordExtended = {
                ...accountRecord,
                LinkedWallets: linkedWallets
            };

            const mappedRecord: AccountRecordMapped = {
                account: accountWithLinkedWallets,
                accountStats: linkedAccountPositions as any
            };

            return mappedRecord;
        } else return { account: undefined };
    }
);
export const getAllMappedWalletByAccountId = createSelector(
    [ getAccounts, getAccountId, getAccountClientGroups, getAccountPositions, getAccountRelationships ],
    (accounts, accountId, accountClientGroups, accountPositions, accountRelationshipsList) => {
        const accountRecord = Object.keys(accounts)
            .map(id => accounts[id])
            .find(wallet => !wallet.IsDeleted &&
                wallet.AccountId === accountId &&
                wallet.AccountTypeId === AccountType.CLIENT_EOA);

        const relatedAccounts = Object.keys(accountRelationshipsList)
            .map(id => accountRelationshipsList[id])
            .filter(relationship => relationship.AccountId2 === accountRecord?.AccountId ||
                relationship.AccountId1 === accountRecord?.AccountId);

        const linkedAccount = relatedAccounts.map(relation => {
            return Object.keys(accounts)
                .map(id => accounts[id])
                .find(account => (account.AccountId === relation.AccountId2 || account.AccountId === relation.AccountId1) &&
                    account.AccountTypeId === AccountType.CLIENT_BDA);
        });

        if (accountRecord) {
            const positionsForAccount = accountPositions[accountRecord.AccountId] ?? {};
            const linkedAccountPositions = Object.keys(positionsForAccount)
                .map(id => positionsForAccount[id]);
            const accountWithAcg: WalletRecordExtended = {
                ...accountRecord,
                ACGNumber1: accountClientGroups[accountRecord.ACGId1] && accountClientGroups[accountRecord.ACGId1].ACGNumber,
                ACGNumber2: accountClientGroups[accountRecord.ACGId2] && accountClientGroups[accountRecord.ACGId2].ACGNumber,
                ACGNumber3: accountClientGroups[accountRecord.ACGId3] && accountClientGroups[accountRecord.ACGId3].ACGNumber,
                LinkedAccount: linkedAccount[0]
            };

            const mappedRecord: WalletRecordMapped = {
                wallet: accountWithAcg,
                walletStats: linkedAccountPositions
            };

            return mappedRecord;
        } else return {
            wallet: undefined,
            walletStats: undefined
        };
    }
);

export const getLinkedDBAccountByEOAAccountId = createSelector(
    [ getAccounts, getAccountRelationships, getAccountId ],
    (accounts, accountRelationshipsList, accountId) => {
        const accountRecord = Object.keys(accounts)
            .map(id => accounts[id])
            .find(wallet => !wallet.IsDeleted &&
                wallet.AccountId === accountId &&
                wallet.AccountTypeId === AccountType.CLIENT_EOA);

        const relatedAccounts = Object.keys(accountRelationshipsList)
            .map(id => accountRelationshipsList[id])
            .filter(relationship => relationship.AccountId2 === accountRecord?.AccountId ||
                relationship.AccountId1 === accountRecord?.AccountId);

        return accountId !== relatedAccounts[0]?.AccountId1 ?
            relatedAccounts[0]?.AccountId1 || ''
            : relatedAccounts[0]?.AccountId2 || '';
    }
);

export const getOpenMappedWalletsByFirmId = createSelector(
    [ getAccounts, getFirmId, getAccountClientGroups, getAccountPositions, getAccountRelationships ],
    (accounts, firmId, accountClientGroups, accountPositions, accountRelationshipsList): WalletRecordMapped[] => {
        const walletsForFirm = Object.keys(accounts)
            .map(id => accounts[id])
            .filter(account => (
                !account.IsDeleted &&
                !account.IsClosed &&
                account.FirmId === firmId &&
                account.AccountTypeId === AccountType.CLIENT_EOA
            ));

        return walletsForFirm.map((wallet: WalletRecordExtended) => {
            const relatedAccounts = Object.keys(accountRelationshipsList)
                .map(id => accountRelationshipsList[id])
                .filter(relationship => relationship.AccountId2 === wallet.AccountId ||
                    relationship.AccountId1 === wallet.AccountId);

            const linkedAccount = relatedAccounts.map(relation => {
                return Object.keys(accounts)
                    .map(id => accounts[id])
                    .find(account => (account.AccountId === relation.AccountId2 || account.AccountId === relation.AccountId1) &&
                        account.AccountTypeId === AccountType.CLIENT_BDA);
            });
            const positionsForWallet = accountPositions[wallet.AccountId] ?? {};
            const linkedAccountPositions = Object.keys(positionsForWallet)
                .map(id => positionsForWallet[id]);

            const walletWithAcg: WalletRecordExtended = {
                ...wallet,
                ACGNumber1: accountClientGroups[wallet.ACGId1] && accountClientGroups[wallet.ACGId1].ACGNumber,
                ACGNumber2: accountClientGroups[wallet.ACGId2] && accountClientGroups[wallet.ACGId2].ACGNumber,
                ACGNumber3: accountClientGroups[wallet.ACGId3] && accountClientGroups[wallet.ACGId3].ACGNumber,
                LinkedAccount: linkedAccount[0]
            };

            return {
                wallet: walletWithAcg,
                walletStats: linkedAccountPositions
            };
        });
    }
);

export const getAllMappedAccountsByFirmId = createSelector(
    [ getAccounts, getFirmId ],
    (accounts, firmId): AccountRecordExtended[] => {
        const accountsForFirm = Object.keys(accounts)
            .map(id => accounts[id])
            .filter(account => !account.IsDeleted && account.FirmId === firmId &&
                account.AccountTypeId === AccountType.CLIENT_BDA);

        return accountsForFirm;
    }
);

export const getAllMappedWalletsByFirmId = createSelector(
    [ getAccounts, getFirmId, getAccountClientGroups, getAccountPositions, getAccountRelationships ],
    (accounts, firmId, accountClientGroups, accountPositions, accountRelationshipsList): WalletRecordMapped[] => {
        const walletsForFirm = Object.keys(accounts)
            .map(id => accounts[id])
            .filter(account => (
                !account.IsDeleted &&
                account.FirmId === firmId &&
                account.AccountTypeId === AccountType.CLIENT_EOA
            ));

        return walletsForFirm.map((wallet: WalletRecordExtended) => {
            const relatedAccounts = Object.keys(accountRelationshipsList)
                .map(id => accountRelationshipsList[id])
                .filter(relationship => relationship.AccountId2 === wallet.AccountId ||
                    relationship.AccountId1 === wallet.AccountId);

            const linkedAccount = relatedAccounts.map(relation => {
                return Object.keys(accounts)
                    .map(id => accounts[id])
                    .find(account => (account.AccountId === relation.AccountId2 || account.AccountId === relation.AccountId1) &&
                        account.AccountTypeId === AccountType.CLIENT_BDA);
            });
            const positionsForWallet = accountPositions[wallet.AccountId] ?? {};
            const linkedAccountPositions = Object.keys(positionsForWallet)
                .map(id => positionsForWallet[id]);

            const walletWithAcg: WalletRecordExtended = {
                ...wallet,
                ACGNumber1: accountClientGroups[wallet.ACGId1] && accountClientGroups[wallet.ACGId1].ACGNumber,
                ACGNumber2: accountClientGroups[wallet.ACGId2] && accountClientGroups[wallet.ACGId2].ACGNumber,
                ACGNumber3: accountClientGroups[wallet.ACGId3] && accountClientGroups[wallet.ACGId3].ACGNumber,
                LinkedAccount: linkedAccount[0]
            };

            return {
                wallet: walletWithAcg,
                walletStats: linkedAccountPositions
            };
        });
    }
);

export const getAccountByUserAccount = createSelector([ getAccounts, getUserAccount ], (accounts, userAccount) => {
    if (!userAccount) return;
    const accountId = userAccount.AccountId;

    return accounts[accountId];
});

export const getTransactionAccount = createSelector([ getAllWalletsArray, getTransaction ], (accounts, transaction) => {
    if (!transaction) return {} as AccountRecordExtended;

    let eoaAddress = '';
    if (
        transaction.type === TransactionTypeChar.transfer ||
        transaction.type === TransactionTypeChar.send ||
        transaction.type === TransactionTypeChar.deposit ||
        transaction.type === TransactionTypeChar.order
    ) {
        eoaAddress = transaction.SenderAddress;
    } else if (
        transaction.type === TransactionTypeChar.receive ||
        transaction.type === TransactionTypeChar.withdrawal ||
        transaction.type === TransactionTypeChar.int_distribution
    ) {
        eoaAddress = transaction.RecipientAddress;
    }

    return accounts.find(acc => acc.Account === eoaAddress);
});

export const getPledgeAccountDetail =
createSelector([ getAllWalletsArray, getPledge, getPledgeDetail ], (accounts, pledge, pledgeDetail) => {
    if (!pledge) return {} as AccountRecordExtended;

    let eoaAddress = '';

    if (pledgeDetail && Object.keys(pledgeDetail).length > 0) {
        if (pledge.Direction === 'Sent') {
            eoaAddress = pledgeDetail.PledgingAccountAddress;
        } else {
            eoaAddress = pledgeDetail.PledgeParticipant[0]?.PledgeParticipantAccountAddress;
        }

        return accounts.find(acc => acc.Account === eoaAddress);
    } else {
        return {} as AccountRecordExtended;
    }
});